import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CircleNotch, Users, EnvelopeSimple, ArrowClockwise } from 'phosphor-react';
import { ENABLE_GOOGLE_AUTH } from '../lib/featureFlags';
import {
  buildAuthPageUrl,
  getRedirectDestination,
  normalizeProductValue,
  resolvePostAuthDestination,
  setPendingAcquisitionProduct,
  setPendingProductIntent,
} from '../lib/productRouting';

// Vertical logo (brain + GENI)
const LOGO_VERTICAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/zc7oaxip_brain%20%2B%20GENICERTO.png";

// Google Icon component
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

export default function Signup() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const product = normalizeProductValue(searchParams.get('product'));
  const allowAcquisition = product === 'options';
  
  // Email confirmation state
  const [confirmationRequired, setConfirmationRequired] = useState(false);
  const [confirmedEmail, setConfirmedEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  
  const { signUp, signInWithGoogle, resendConfirmation } = useAuth();
  const navigate = useNavigate();
  const isSubmittingRef = useRef(false);

  // Calculate redirect destination once
  const redirectDestination = getRedirectDestination(searchParams, location.state);

  // Check for referral code in URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      // Store in localStorage for profile creation
      localStorage.setItem('referral_code', refCode.toUpperCase());
    }
  }, [searchParams]);

  useEffect(() => {
    if (product) {
      setPendingProductIntent(product);
    }
  }, [product]);

  const handleGoogleSignUp = async () => {
    if (googleLoading) return;
    
    setGoogleLoading(true);
    setError('');
    
    try {
      if (allowAcquisition) {
        setPendingAcquisitionProduct(product);
      }
      // Pass the intended redirect destination to OAuth flow
      await signInWithGoogle(redirectDestination, { allowAcquisition });
      // OAuth will redirect, so we don't need to navigate here
    } catch (err) {
      setError('Failed to connect with Google. Please try again.');
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions - check both ref and state
    if (isSubmittingRef.current || loading || cooldown) {
      return;
    }
    
    isSubmittingRef.current = true;
    setLoading(true);
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      isSubmittingRef.current = false;
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      isSubmittingRef.current = false;
      return;
    }

    try {
      if (allowAcquisition) {
        setPendingAcquisitionProduct(product);
      }
      const result = await signUp(email, password);
      
      // Check if user already exists (identities array is empty)
      if (result?.user?.identities?.length === 0) {
        setError('An account with this email already exists');
      } else if (result?.confirmationRequired) {
        // Email confirmation is required
        setConfirmedEmail(email);
        setConfirmationRequired(true);
      } else if (result?.user && result?.session) {
        // Auto-login successful (email confirmation disabled)
        // Username is auto-generated on backend
        const destination = await resolvePostAuthDestination({
          requestedRedirect: redirectDestination,
          allowAcquisition,
        });
        navigate(destination, { replace: true });
      } else if (result?.user) {
        // User created but no session - treat as confirmation required
        setConfirmedEmail(email);
        setConfirmationRequired(true);
      }
    } catch (err) {
      // Show actual error message
      setError(err.message || 'Failed to sign up');
      
      // Add cooldown for rate limit errors
      if (err.message?.toLowerCase().includes('too many') || 
          err.message?.toLowerCase().includes('rate') ||
          err.message?.toLowerCase().includes('wait')) {
        setCooldown(true);
        setTimeout(() => setCooldown(false), 5000);
      }
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const handleResendConfirmation = async () => {
    if (resending || !confirmedEmail) return;
    
    setResending(true);
    setResendSuccess(false);
    setError('');
    
    try {
      await resendConfirmation(confirmedEmail);
      setResendSuccess(true);
      // Reset success message after 5 seconds
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err) {
      setError(err.message || 'Failed to resend confirmation email');
    } finally {
      setResending(false);
    }
  };

  // Email confirmation success state
  if (confirmationRequired) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo - Vertical Version */}
          <div className="flex flex-col items-center justify-center mb-8">
            <img
              src={LOGO_VERTICAL}
              alt="GENI"
              className="h-20 md:h-24 lg:h-28 w-auto object-contain mx-auto"
            />
          </div>

          <div className="bg-[#121212] border border-white/10 rounded-sm p-8">
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-[#00E676]/10 flex items-center justify-center">
                <EnvelopeSimple size={32} weight="duotone" className="text-[#00E676]" />
              </div>
            </div>

            <h2 className="text-2xl font-bold font-heading text-white text-center mb-2">
              Check your email
            </h2>
            
            <p className="text-white/60 text-center mb-6">
              We sent a confirmation link to<br />
              <span className="text-white font-medium">{confirmedEmail}</span>
            </p>

            <div className="bg-white/5 border border-white/10 rounded-sm p-4 mb-6">
              <p className="text-white/50 text-sm text-center">
                Click the link in the email to activate your account. 
                Check your spam folder if you don't see it.
              </p>
            </div>

            {/* Resend Section */}
            <div className="text-center space-y-3">
              {resendSuccess && (
                <div className="bg-[#00E676]/10 border border-[#00E676]/30 rounded-sm p-3 mb-3">
                  <p className="text-[#00E676] text-sm">Confirmation email resent!</p>
                </div>
              )}
              
              {error && (
                <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm p-3 mb-3">
                  <p className="text-[#FF3B30] text-sm">{error}</p>
                </div>
              )}

              <p className="text-white/40 text-sm">Didn't receive the email?</p>
              
              <Button
                onClick={handleResendConfirmation}
                disabled={resending}
                variant="outline"
                className="border-white/20 text-white/80 hover:bg-white/5 hover:text-white"
                data-testid="resend-confirmation"
              >
                {resending ? (
                  <span className="flex items-center gap-2">
                    <CircleNotch size={16} className="animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <ArrowClockwise size={16} />
                    Resend confirmation email
                  </span>
                )}
              </Button>
            </div>

            <div className="mt-8 pt-6 border-t border-white/10">
              <p className="text-center text-white/60 text-sm">
                Already confirmed?{' '}
                <Link to={buildAuthPageUrl('/login', redirectDestination, product)} className="text-[#00E676] hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo - Vertical Version */}
        <div className="flex flex-col items-center justify-center mb-8">
          <img
            src={LOGO_VERTICAL}
            alt="GENI"
            className="h-20 md:h-24 lg:h-28 w-auto object-contain mx-auto"
          />
        </div>

        <div className="bg-[#121212] border border-white/10 rounded-sm p-8">
          <h2 className="text-3xl font-bold font-heading text-white mb-2">Create Account</h2>
          <p className="text-white/60 mb-6">Turn your trading data into clear, statistical insights to improve your performance.</p>

          {/* Referral Banner */}
          {referralCode && (
            <div className="bg-[#00E676]/10 border border-[#00E676]/20 rounded-sm p-3 mb-6 flex items-center gap-3">
              <Users size={20} className="text-[#00E676]" />
              <div>
                <p className="text-[#00E676] text-sm font-medium">Referred by a friend!</p>
                <p className="text-white/50 text-xs">Code: {referralCode}</p>
              </div>
            </div>
          )}

          {/* Google Sign Up Button - Controlled by feature flag */}
          {ENABLE_GOOGLE_AUTH && (
            <>
              <Button
                type="button"
                onClick={handleGoogleSignUp}
                disabled={googleLoading || loading}
                className="w-full bg-white text-gray-700 font-medium hover:bg-gray-100 transition-colors rounded-sm mb-4 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="google-signup"
              >
                {googleLoading ? (
                  <CircleNotch size={18} className="animate-spin text-gray-600" />
                ) : (
                  <GoogleIcon />
                )}
                {googleLoading ? 'Connecting...' : 'Continue with Google'}
              </Button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-[#121212] px-3 text-white/40">or</span>
                </div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-white/80 mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                disabled={loading}
                className="bg-[#0A0A0A] border-white/10 text-white"
                data-testid="signup-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-white/80 mb-2 block">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={loading}
                className="bg-[#0A0A0A] border-white/10 text-white"
                data-testid="signup-password"
              />
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-white/80 mb-2 block">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={loading}
                className="bg-[#0A0A0A] border-white/10 text-white"
                data-testid="signup-confirm-password"
              />
            </div>

            {/* Passive Terms Consent */}
            <p className="text-xs text-white/40 pt-1">
              By signing up, you agree to the{' '}
              <Link to="/terms" className="text-white/60 hover:text-[#00E676] underline" target="_blank">
                Terms of Use
              </Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-white/60 hover:text-[#00E676] underline" target="_blank">
                Privacy Policy
              </Link>.
            </p>

            {error && (
              <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm p-3" data-testid="signup-error">
                <p className="text-[#FF3B30] text-sm">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || cooldown}
              className="w-full bg-white text-black font-bold hover:bg-gray-200 transition-colors rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="signup-submit"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <CircleNotch size={18} className="animate-spin" />
                  Creating account...
                </span>
              ) : cooldown ? (
                'Please wait...'
              ) : (
                'Create Account'
              )}
            </Button>

            <p className="text-center text-white/60 text-sm">
              Already have an account?{' '}
              <Link 
                to={buildAuthPageUrl('/login', redirectDestination, product)} 
                className="text-[#00E676] hover:underline"
              >
                Sign in
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
